<template>
  <teleport to="body">
    <transition name="fade">
      <div class="fixed top-0 left-0 w-full h-full bg-backdrop flex justify-center items-center z-100 p-15"
           @click.self="close"
      >
        <div class="relative bg-primary max-w-500 w-full rounded-8 p-16 pb-25">
          <div class="flex justify-end">
            <i class="icon-close-smooth text-app-color text-28 cursor-pointer opacity-50 hover:opacity-100 anim-opacity" @click="close" />
          </div>
          <!-- border-b border-grey-800 -->
          <div class="p-20 pb-15 pt-0">
            <div class="text-app-color text-center">
              <img class="h-50 max-w-full mx-auto" src="@/assets/images/invite_teammates.svg" alt="teammates">
              <p class="text-22 font-bold text-center mt-12">Invite your team members</p>
            </div>

            <InputModule
              ref="emailsRef"
              v-model="emails"
              name="email"
              type="text"
              label="Emails"
              placeholder="name@example.com"
              class="w-full mt-20"
              input-classes="px-22 py-12 rounded-6 border border-grey-10 anim-all text-15"
              label-class="mb-5 font-bold text-15 text-app-color"
              shadow="focus:shadow-signup"
              :has-background="false"
            />
            <p class="text-12 text-grey-950 mt-7">Separate multiple emails with commas</p>

            <BooksSelect
              :selected-books="selectedBooks"
              :options="options"
              top="top-80"
              :default-book-id="options[0].id"
              @onSelect="onSelect"
              @toggle="toggleModal"
              @close="closeModal"
            >
              <InputModule
                v-model="selectedBooksTitle"
                label="Invite to..."
                placeholder="Headquarters"
                class="w-full mt-20"
                readonly
                :shadow="openBookModal ? 'shadow-signup' : ''"
                input-classes="px-22 py-12 rounded-6 border border-grey-10 anim-all text-15 book-input"
                label-class="mb-5 font-bold text-15 text-app-color"
                :has-background="false"
              />
            </BooksSelect>

            <ButtonModule
              class="bg-brand hover:bg-brand-hover rounded-6 px-35 py-16 text-white text-15 w-full mt-30 font-semibold"
              @click="invite"
            >
              <p class="text-center w-full">Send invitations</p>
            </ButtonModule>
          </div>

          <InviteLink v-if="inviteLink" :link="inviteLink" />
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { computed, defineComponent, nextTick, onMounted, ref } from 'vue'

import { useStore } from 'vuex'
import { teamsService } from '@/services'
import { preventBodyScroll } from '@/core/helper-functions'

import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import InputModule from '@/components/shared/form-controls/InputModule'
import InviteLink from '@/components/shared/InviteLink'
import BooksSelect from '@/components/task-board/BooksSelect'

import { emailValidation } from '@/helpers'

export default defineComponent({
  components: { ButtonModule, InputModule, BooksSelect, InviteLink },

  props: {
    book: Object
  },

  emits: ['close'],

  setup (props, { emit }) {
    const { state, getters, commit } = useStore()

    const emailsRef = ref(null)
    const emails = ref('')
    const selectedBooks = ref([])
    const selectedBooksTitle = ref('')
    const openBookModal = ref(false)

    const inviteLink = computed(() => state.team.team?.inviteLink)

    const isDarkMode = computed(() => getters.isDarkMode)

    const options = computed(() => state.book.books)

    onMounted(() => {
      nextTick(() => emailsRef.value?.focus())
    })

    function close () {
      preventBodyScroll(false)
      emit('close')
    }

    function onSelect (value) {
      selectedBooks.value = value
      selectedBooksTitle.value = value[0] ? value.map(item => item.title).join(', ') : ''
    }

    function checkEmail (email) {
      // eslint-disable-next-line no-useless-escape
      const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return re.test(String(email.trim()).toLowerCase())
    }

    function invite () {
      const emailsArray = emails.value.split(',').filter(item => checkEmail(item)).map(item => item.trim())
      const bookIds = selectedBooks.value.map(item => item.id)

      if (emailsArray.length > 0 && bookIds.length > 0) {
        emailsArray.forEach(item => {
          emailValidation(item)
        })

        teamsService.inviteMembers({ emails: emailsArray, bookIds }).then(() => {
          emails.value = ''
          commit('setNotification', {
            isVisible: true,
            message: 'Invite successfully sent'
          })
        }).catch((err) => {
          const error = err.data.error.message.split('emails[')

          if (error.length > 0) {
            const index = parseInt((error[1]))

            commit('setNotification', {
              isVisible: true,
              message: `Email ${emailsArray[index]} is not valid`
            })
          }
        })
      }
    }

    function toggleModal (value) {
      openBookModal.value = value
    }

    function closeModal () {
      openBookModal.value = false
    }

    return {
      // ref
      emailsRef,
      // data
      emails,
      openBookModal,
      selectedBooks,
      selectedBooksTitle,
      // computed
      isDarkMode,
      options,
      inviteLink,
      // function
      close,
      invite,
      onSelect,
      toggleModal,
      closeModal
    }
  }
})
</script>
